import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { Claim } from '../../types/report';
import { useReportPanel, CHAT_UNSCOPED } from '../../state/reportPanel';
import { getClaim } from '../../services/reportService';
import { panelForType, TierPanel, TIER_DRILL_PREFIX } from './panels/panelTemplates';
import type { PanelActions } from './panels/panelTemplates';
import { ReportChatPanel } from './ReportChatPanel';

interface DrillPanelProps {
  reportId: string;
  claims: Record<string, Claim>; // claims already inlined in the document
}

/** Right slide-over (480px, overlay — the report does not reflow). One panel at
 * a time; content replaces on a new claim. Closes on Esc / backdrop / button,
 * restoring scroll (handled in reportPanel state). Header/definition render from
 * the inlined claim immediately; if a claim isn't inlined it lazy-fetches. */
export function DrillPanel({ reportId, claims }: DrillPanelProps) {
  const { activeClaimId, mode, setMode, chatBusy, open, openChat, close, scrollToSection } =
    useReportPanel();
  const [fetched, setFetched] = useState<Claim | null>(null);

  const isChatScope = activeClaimId === CHAT_UNSCOPED;
  const isTierDrill = !!activeClaimId && activeClaimId.startsWith(TIER_DRILL_PREFIX);
  const inlined = activeClaimId && !isChatScope && !isTierDrill ? claims[activeClaimId] : null;
  const claim = inlined ?? fetched;

  // actions threaded into the panel templates (Ask CTA, tier drill, related-chip
  // replace/scroll). Chat-mode entry keeps the panel open, just swaps content.
  const actions: PanelActions = {
    openClaim: (id: string) => open(id),
    openChat: (id: string) => openChat(id),
    scrollToSection,
  };

  // tier-drill: 2-3 example claims of the clicked tier (for the explainer panel)
  const tier = isTierDrill ? activeClaimId!.slice(TIER_DRILL_PREFIX.length) : '';
  const tierExamples = isTierDrill
    ? Object.values(claims)
        .filter((c) => c.tier === tier && c.header?.title)
        .slice(0, 3)
        .map((c) => ({ id: c.id, title: String(c.header.title) }))
    : [];

  useEffect(() => {
    if (activeClaimId && !isChatScope && !isTierDrill && !inlined) {
      let active = true;
      // Guarded: a failed/rejected fetch must not crash the panel — it falls
      // back to the "Detail unavailable" state (claim stays null).
      Promise.resolve(getClaim(reportId, activeClaimId))
        .then((c) => {
          if (active) setFetched(c ?? null);
        })
        .catch(() => {
          if (active) setFetched(null);
        });
      return () => {
        active = false;
      };
    }
    setFetched(null);
  }, [activeClaimId, isChatScope, isTierDrill, inlined, reportId]);

  const isOpen = activeClaimId !== null;
  // Chat tab is available whenever a claim is open (scope it) or the panel was
  // opened in chat mode. Details tab is hidden for the unscoped chat sentinel.
  const showDetails = !isChatScope;

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(o) => {
        if (!o) close();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/10" />
        <Dialog.Content
          data-testid="drill-panel"
          className="report-root drill-panel-root"
          aria-describedby={undefined}
        >
          <div className="drill-panel-topbar" role="tablist">
            {/* While a chat answer is loading, disable Details so the in-progress
                conversation isn't hidden mid-flight (it's preserved either way). */}
            {showDetails && (
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'details'}
                disabled={chatBusy}
                className={`drill-panel-tab${mode === 'details' ? ' active' : ''}`}
                onClick={() => setMode('details')}
              >
                Details
              </button>
            )}
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'chat'}
              className={`drill-panel-tab${mode === 'chat' ? ' active' : ''}`}
              onClick={() => setMode('chat')}
            >
              Chat
            </button>
            <Dialog.Close asChild>
              <button aria-label="Close" data-testid="panel-close" className="drill-panel-close">
                <X size={18} />
              </button>
            </Dialog.Close>
          </div>

          <Dialog.Title className="sr-only">{claim?.header.title ?? 'Detail'}</Dialog.Title>

          <div className="drill-panel-body">
            {mode === 'chat' ? (
              <ReportChatPanel reportId={reportId} claims={claims} />
            ) : isTierDrill ? (
              <TierPanel tier={tier} examples={tierExamples} actions={actions} />
            ) : claim ? (
              panelForType(claim, actions)
            ) : (
              <div className="panel-empty">Detail unavailable.</div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
